import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import Header from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import { BreadcrumbItem } from '../components/Header';
import { progressService } from '../services/progressService';
import { isAuthenticationError } from '../utils/authError';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';
import Step5InventoryPresentationFields from '../components/Step5InventoryPresentationFields';
import {
  normalizeStep5InventoryPresentation,
  Step5InventoryPresentationRecord,
} from '../types/step5InventoryPresentation';

interface Step5Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step5: React.FC<Step5Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isExamplesOpen, setIsExamplesOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Five",
      description: "Discover why revealing the exact nature of my wrongs brings release and clarity",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "How to work Step Five (guidance & video)",
      description: "Read guidance and watch video on how to work through Step Five",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Present my Step Four inventories to my sponsor",
      description:
        "Sit down with your sponsor in person or online and go through your inventories out loud — entry by entry. Your sponsor listens, encourages, and may take notes. Some members need several meetings to finish. You and your sponsor may work this and the next 2 actions in this step in whatever order fits your process. Check this off when you've completed presenting your inventories (or when you're satisfied with where you are for now).",
      completed: false
    },
    {
      id: 4,
      title: "Identify my character defects",
      description:
        "With your sponsor's help, name the character defects that showed up in your inventory. You may notice many defects, but most members focus on their 3–6 most impactful ones for this pass through the steps. Enter your initial list here - we will work on these more in Steps Six and Seven.",
      completed: false
    },
    {
      id: 5,
      title: "Share and review my character defects with my sponsor",
      description:
        "If you and your sponsor haven't already agreed on the character defects you'll work on this time through the steps, update your sponsor on your list. Check this off when you've discussed it — the share button is optional for a written summary.",
      completed: false
    },
    {
      id: 6,
      title: "Take Step Five",
      description: "Admitted to God, to myself, and to another human being the exact nature of my wrongs.",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);

  // State for Step Five work
  const [characterDefects, setCharacterDefects] = useState<string[]>([]);
  const [inventoryPresentation, setInventoryPresentation] = useState<Step5InventoryPresentationRecord>({});
  const presentationSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Auto-save state management
  const [savingDefects, setSavingDefects] = useState(false);
  const [savedDefects, setSavedDefects] = useState(false);
  const [saveDefectsError, setSaveDefectsError] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getShareContext = useCallback(
    () => ({
      step5InventoryPresentation: inventoryPresentation,
      characterDefects: characterDefects.filter((d) => d.trim().length > 0),
    }),
    [inventoryPresentation, characterDefects]
  );

  const { openShareDialog, shareDialogProps } = useStepShareDialog(5, user, actions, getShareContext);
  
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Five', path: '/saa/12-steps/step-5', active: true }
  ];

  const handleGreenBookClick = () => {
    setIsContentReaderOpen(true);
  };

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step5', 'refreshStepData start');
      const { steps, journeyProgress } = await perfTimeAsync('Step5 refreshStepData (total)', () =>
        progressService.loadStepPagesProgress('SAA', '12-steps', [5], { includeJourney: true })
      );

      const existingProgress = steps[5] ?? null;

      if (journeyProgress) {
        setIsOptedOut(journeyProgress.opted_out);
      }

      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);

        if (savedData.actions) {
          const merged = mergeStepActions(5, originalActions, savedData, !!existingProgress.completed);
          setActions(merged.actions);
          if (merged.migrationApplied) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 5,
              step_title: 'Step Five',
              completed: !!existingProgress.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
            });
          }
        }

        if (savedData.characterDefects) {
          setCharacterDefects(savedData.characterDefects);
        }

        if (savedData.step5_inventory_presentation) {
          setInventoryPresentation(
            normalizeStep5InventoryPresentation(savedData.step5_inventory_presentation)
          );
        }
      }

      setError(null);
      setAuthError(false);
      perfSummary('Step Five page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      if (isAuthenticationError(err)) {
        setAuthError(true);
        setError('Your session has expired. Please log in again to continue.');
      } else {
        setError('Failed to load your progress. Please try refreshing the page.');
      }
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const ACTION_COUNT = originalActions.length;

  const isStep5ActionAvailable = useCallback((actionIndex: number, acts: ActionItem[]) => {
    const isComplete = (id: number) => acts.find((a) => a.id === id)?.completed ?? false;
    if (actionIndex === 0) return true;
    if (actionIndex === 1) return isComplete(1);
    // Actions 3–5 (indices 2–4): available once action 2 is complete
    if (actionIndex >= 2 && actionIndex <= 4) return isComplete(2);
    // Action 6 (index 5): requires actions 1–5 complete
    if (actionIndex === 5) {
      return [1, 2, 3, 4, 5].every(isComplete);
    }
    return acts.slice(0, actionIndex).every((a) => a.completed);
  }, []);

  const hasCharacterDefects = () =>
    characterDefects.some((defect) => defect.trim().length > 0);

  const canCompleteAction = (actionId: number): boolean => {
    if (actionId === 4) return hasCharacterDefects();
    return true;
  };

  const saveStep5Data = useCallback(
    async (
      updatedActions: ActionItem[],
      stepCompleted?: boolean,
      presentation?: Step5InventoryPresentationRecord
    ) => {
      const nonEmptyDefects = characterDefects.filter((d) => d.trim().length > 0);
      const normalizedPresentation = normalizeStep5InventoryPresentation(
        presentation ?? inventoryPresentation
      );
      const completed =
        stepCompleted !== undefined
          ? stepCompleted
          : updatedActions.every((a) => a.completed);
      const extra: Record<string, unknown> = {
        characterDefects: nonEmptyDefects,
      };
      if (
        normalizedPresentation.startedDate ||
        normalizedPresentation.place ||
        normalizedPresentation.reflection
      ) {
        extra.step5_inventory_presentation = normalizedPresentation;
      }
      return progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 5,
        step_title: 'Step Five',
        completed,
        data: stepDataWithActions(updatedActions, getStepActionSchema(5).actionSchemaVersion, extra),
      });
    },
    [characterDefects, inventoryPresentation]
  );

  const persistPresentation = useCallback(
    async (next: Step5InventoryPresentationRecord) => {
      if (!user) return;
      const normalized = normalizeStep5InventoryPresentation({
        ...next,
        updatedAt: new Date().toISOString(),
      });
      setInventoryPresentation(normalized);
      try {
        await saveStep5Data(actions, undefined, normalized);
      } catch (err) {
        console.error('Error saving inventory presentation notes:', err);
        if (isAuthenticationError(err)) {
          setAuthError(true);
          setError('Your session has expired. Please log in again to save your progress.');
        }
      }
    },
    [user, actions, saveStep5Data]
  );

  const handlePresentationChange = useCallback(
    (next: Step5InventoryPresentationRecord) => {
      setInventoryPresentation(next);
      if (presentationSaveTimer.current) clearTimeout(presentationSaveTimer.current);
      presentationSaveTimer.current = setTimeout(() => {
        void persistPresentation(next);
      }, 400);
    },
    [persistPresentation]
  );

  useEffect(() => {
    return () => {
      if (presentationSaveTimer.current) clearTimeout(presentationSaveTimer.current);
    };
  }, []);

  const handleCharacterDefectChange = (index: number, value: string) => {
    const newDefects = [...characterDefects];

    while (newDefects.length <= index) {
      newDefects.push('');
    }

    newDefects[index] = value;

    while (newDefects.length > 0 && newDefects[newDefects.length - 1] === '') {
      newDefects.pop();
    }

    setCharacterDefects(newDefects);
  };

  const handleCharacterDefectKeyPress = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const nextIndex = index + 1;
      const nextInput = document.querySelector(`input[data-defect-index="${nextIndex}"]`) as HTMLInputElement;

      if (nextInput) {
        nextInput.focus();
      } else if (nextIndex < 9) {
        const newDefects = [...characterDefects];
        while (newDefects.length <= nextIndex) {
          newDefects.push('');
        }
        setCharacterDefects(newDefects);

        setTimeout(() => {
          const newInput = document.querySelector(`input[data-defect-index="${nextIndex}"]`) as HTMLInputElement;
          if (newInput) {
            newInput.focus();
          }
        }, 10);
      }
    }
  };

  const autoSaveDefects = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      if (!user) return;

      setSavingDefects(true);
      setSaveDefectsError(false);
      setSavedDefects(false);

      try {
        await saveStep5Data(actions);
        setSavingDefects(false);
        setSavedDefects(true);
        setTimeout(() => setSavedDefects(false), 2000);
      } catch (err) {
        console.error('Error auto-saving character defects:', err);
        setSavingDefects(false);
        setSaveDefectsError(true);
        setTimeout(() => setSaveDefectsError(false), 3000);
      }
    }, 300);
  };

  const renderCharacterDefectFields = () => (
    <div className={styles.actionContentIndent}>
      <div style={{ marginBottom: 16, width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <label style={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>
            My character defects
          </label>
          <button
            type="button"
            onClick={() => setIsExamplesOpen(true)}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Character defect examples
          </button>
        </div>
        <div style={{ marginBottom: 16 }}>
          {Array.from({ length: Math.max(characterDefects.length + 1, 1) }, (_, idx) => {
            if (idx >= 9) return null;

            let placeholder = '';
            if (characterDefects.length === 0 && idx === 0) {
              placeholder = 'Enter a character defect...';
            } else if (idx === characterDefects.length) {
              placeholder = 'Add another character defect...';
            }

            return (
              <input
                key={idx}
                type="text"
                value={characterDefects[idx] || ''}
                onChange={(e) => handleCharacterDefectChange(idx, e.target.value)}
                onKeyPress={(e) => handleCharacterDefectKeyPress(idx, e as React.KeyboardEvent)}
                onBlur={() => autoSaveDefects()}
                data-defect-index={idx}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
                placeholder={placeholder}
              />
            );
          })}
        </div>

        <div
          style={{
            textAlign: 'right',
            minHeight: 24,
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {savingDefects && <span style={{ color: '#6c757d' }}>Saving...</span>}
          {savedDefects && <span style={{ color: '#28a745' }}>✓ All changes saved</span>}
          {saveDefectsError && (
            <span style={{ color: '#dc3545' }}>⚠ Save failed. Please try again.</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderActionContent = (action: ActionItem) => {
    if (action.id === 3) {
      return (
        <div className={styles.actionContentIndent}>
          <Step5InventoryPresentationFields
            value={inventoryPresentation}
            onChange={handlePresentationChange}
          />
        </div>
      );
    }
    if (action.id === 4) {
      return renderCharacterDefectFields();
    }
    return null;
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      setError('User not logged in. Please log in.');
      return;
    }

    const action4 = updatedActions.find((a) => a.id === 4);
    const currentAction4 = actions.find((a) => a.id === 4);

    if (action4 && !currentAction4?.completed && action4.completed && !hasCharacterDefects()) {
      return;
    }

    try {
      setActions(updatedActions);

      const allCompleted = updatedActions.every((action) => action.completed);
      const updatedStepData = await saveStep5Data(updatedActions, allCompleted);

      if (updatedStepData?.step) {
        setStepData(updatedStepData.step);
      }

      const completedCount = updatedActions.filter((a) => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Five - ${completedCount} of ${ACTION_COUNT} actions completed`,
        progress_milestone: allCompleted ? 'step5_completed' : 'step5_in_progress',
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      if (isAuthenticationError(err)) {
        setAuthError(true);
        setError('Your session has expired. Please log in again to save your progress.');
      } else {
        setError('Failed to save progress. Please try again.');
      }
    }
  };

  const allCompleted = actions.every((action) => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Five</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={5}
                stepTitle="Step Five"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            "Admitted to God, to ourselves, and to another human being the exact nature of our wrongs."
          </p>
          

          

        </div>

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
            {authError ? (
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button 
                  onClick={onLogin} 
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Log In Again
                </button>
                <button 
                  onClick={() => window.location.reload()} 
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Refresh Page
                </button>
              </div>
            ) : (
              <button onClick={() => window.location.reload()}>Refresh Page</button>
            )}
          </div>
        )}

        {!user && !isInitializing && !error && <StepLoginPrompt onLogin={onLogin} />}

        {user && isInitializing && !error && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {user && !isInitializing && !error && (
          <>
            <StepActions
            actions={withShareButton(5, actions, openShareDialog)}
            onActionChange={handleActionChange}
            renderActionContent={renderActionContent}
            stepNumber={5}
            canCompleteAction={canCompleteAction}
            isActionAvailable={isStep5ActionAvailable}
          />

        {allCompleted && (
          <>
            <div className={styles.completionMessage}>
              <h3>🎉 Congratulations!</h3>
              <p>You have completed all actions for Step Five and can move on to Step Six.</p>
            </div>
            <div className={styles.stepNavigation}>
              <div className={styles.navigationLinks}>
                <a href="/saa/12-steps/step-4" className={styles.prevStepLink}>
                  {'<<'} Step Four
                </a>
                <span></span>
                <a href="/saa/12-steps/step-6" className={styles.nextStepLink}>
                  Step Six {'>>'}
                </a>
              </div>
            </div>
          </>
        )}

        {!allCompleted && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-4" className={styles.prevStepLink}>
                {'<<'} Step Four
              </a>
              <span></span>
              <a href="/saa/12-steps/step-6" className={styles.nextStepLink}>
                Step Six {'>>'}
              </a>
            </div>
          </div>
        )}
          </>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-five start>>"
        endTag="<<step-five end>>"
      />

      {/* ContentReader for How to Work Step Five */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_5_Guidance.md"
        startTag="<<working-step-five start>>"
        endTag="<<working-step-five end>>"
        title="Guidance on working Step Five"
      />

      {/* ContentReader for Examples */}
      <ContentReader
        isOpen={isExamplesOpen}
        onClose={() => setIsExamplesOpen(false)}
        filePath="/content/character_defects_step6.md"
        startTag=""
        endTag=""
        title="Example character defects / shortcomings"
      />

      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="IRYVP5A6XmM"
        title="Step Five Video Guide"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={5}
        stepTitle="Step Five"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          if (user) {
            void refreshStepData();
          }
        }}
      />
      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step5; 
